import React from 'react'
import classNames from 'classnames'
import {Select} from 'antd'
import {IoInformationCircleOutline} from 'react-icons/io5'
import {CourseSelector} from 'views/AppLocals/Course/components/CourseSelector'
import {CourseClassSelector} from 'views/AppLocals/Course/components/CourseClassSelector'

// Các mảnh dùng CHUNG cho cả 3 entity (Học viên/Điểm danh/Điểm số) của màn "Xuất/Nhập hàng loạt" -
// tách ra khỏi CourseImportExportPage.js (đã đủ nặng khi có 3 entity) để StudentPanels.js/
// AttendancePanels.js/GradePanels.js cùng import lại, KHÔNG lặp code.

export const SUB_TABS = {EXPORT: 'export', IMPORT: 'import'}

export const InfoBanner = ({children}) => (
    <div className="p-3 flex items-start gap-2 rounded-xl bg-amber-50 text-xs text-amber-700 leading-snug">
        <IoInformationCircleOutline size={15} className="flex-shrink-0 mt-0.5"/>
        <span>{children}</span>
    </div>
)

export const RequiredMark = () => <span className="text-rose-500">*</span>

// InnerWrapper riêng, cao tối thiểu 46px (khớp cỡ "large" của antd Input/Select) - InnerWrapper mặc
// định của ContainerPicker (BaseSelector.js, dùng CHUNG cho rất nhiều selector khác trong app) không
// set minHeight nên co theo nội dung, thấp hơn hẳn ô KHÓA ĐÀO TẠO cạnh bên (CourseSelector.js cũng tự
// override y hệt kiểu này) - override cục bộ ở ĐÂY thay vì sửa InnerWrapper dùng chung, tránh đổi
// chiều cao mọi selector khác trong app ngoài ý muốn.
const InnerWrapperLarge = ({children}) => (
    <div style={{minHeight: 46}} className="flex flex-col justify-center border border-color-100 background hover:background-50 rounded-lg">
        {children}
    </div>
)

// Chọn Khóa VÀ Lớp - CẢ 2 BẮT BUỘC (không optional/dùng lớp mặc định - cho rõ ràng luồng: 1 file
// Xuất/Nhập luôn khoá cứng đúng 1 Khóa + 1 Lớp, xem StudentDefinition/AttendanceDefinition/
// GradeDefinition::columns() bên BE) - dùng chung cho cả 3 entity, cả Xuất & Nhập.
export const CourseClassPicker = ({owner_id, owner_type, course, setCourse, courseClass, setCourseClass, t}) => (
    <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1.5">
            <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('course')} <RequiredMark/></span>
            <CourseSelector
                name="course" value={course} owner_id={owner_id} owner_type={owner_type}
                onChange={(e) => {setCourse(e.target.value); setCourseClass(null)}}
            />
        </div>
        <div className="flex flex-col gap-1.5">
            <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('class')} <RequiredMark/></span>
            {course?.id ? (
                // key={course.id}: ép remount khi đổi Khóa - ContainerPicker (nền của CourseClassSelector)
                // chỉ fetch 1 lần rồi cache theo state isLoaded nội bộ, KHÔNG tự refetch khi query
                // (course_id) đổi mà giữ nguyên instance -> nếu không remount, danh sách Lớp bị kẹt lại
                // của Khóa cũ khi đổi Khóa khác (bạn báo). Fix cục bộ tại đây thay vì sửa ContainerPicker
                // dùng chung (ảnh hưởng nhiều nơi khác) - xem CourseImportExportShared.js đầu file.
                <CourseClassSelector
                    key={course.id}
                    name="course_class" value={courseClass} course_id={course.id} allowClear={false}
                    renderInnerWrapper={InnerWrapperLarge}
                    onChange={(e) => setCourseClass(e.target.value)}
                />
            ) : (
                <div className="h-[46px] flex items-center px-3 rounded-lg border border-dashed border-color-200 text-xs text-color-300 italic">
                    {t('select course first hint')}
                </div>
            )}
        </div>
    </div>
)

// Chọn Môn học - CHỈ dùng cho Điểm danh/Điểm số (Học viên không cần). Đọc thẳng từ
// courseClass.class_subjects (ĐÃ có sẵn trong response course_getCourseClasses_Api mà
// CourseClassSelector đang gọi - xác nhận qua CourseClassTransformerBasic.php - KHÔNG gọi thêm API
// nào) - CỐ Ý không dùng SubjectSelector (liệt kê MỌI môn của cả Khóa, có thể chưa được gán vào Lớp
// đang chọn, sẽ báo lỗi "chưa gán vào lớp" vô lý khi Nhập lại).
export const ClassSubjectPicker = ({courseClass, subject, setSubject, t}) => {
    const options = (courseClass?.class_subjects || [])
        .map((cs) => cs.subject)
        .filter(Boolean)
        .map((s) => ({value: s.id, label: s.title, subject: s}))

    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('subjects')} <RequiredMark/></span>
            {courseClass?.id ? (
                <Select
                    size="large" allowClear
                    placeholder={t('select subject placeholder')}
                    value={subject?.id}
                    onChange={(value) => setSubject(options.find((o) => o.value === value)?.subject || null)}
                    options={options}
                    notFoundContent={<span className="text-xs text-color-300 italic">{t('class has no subjects yet')}</span>}
                />
            ) : (
                <div className="h-[46px] flex items-center px-3 rounded-lg border border-dashed border-color-200 text-xs text-color-300 italic">
                    {t('select course class first hint')}
                </div>
            )}
        </div>
    )
}

// Segmented control rõ trạng thái active (nền đặc + chữ trắng) - Radio.Button mặc định của antd
// trước đây khó phân biệt cái nào đang chọn. Tổng quát hoá nhận options=[{value,label}] để dùng
// chung cho cả 3 entity (Học viên: Nhập mới/Upsert - Điểm danh: Buổi mới/Cập nhật buổi đã có - Điểm
// số: Cột mới/Cập nhật cột đã có), khác nhau ở label/value do từng Panel tự truyền vào.
// mt-1 (thêm chút khoảng trống so với field Khóa/Lớp/Môn học ngay phía trên - nhóm chọn dữ liệu và
// nhóm chọn chế độ nên tách bạch hơn 1 chút thay vì cùng khoảng cách đều tăm tắp với các field khác)
// + items-stretch (thay items-center - label dài như "Upsert (sửa học viên đã có)" xuống 2 dòng vẫn
// cần 2 nút cao bằng nhau, items-center trước đây để nút ngắn hơn lùn hẳn xuống, lệch hẳn với nút
// dài) + min-h-[46px] khớp cỡ "large" của các field phía trên (CourseClassPicker/ClassSubjectPicker).
export const ModeToggle = ({value, onChange, options, t}) => (
    <div className="mt-1 flex items-stretch gap-2">
        {options.map((o) => (
            <button
                key={o.value} type="button" onClick={() => onChange(o.value)}
                className={classNames(
                    'flex-1 min-h-[46px] px-3 py-2.5 flex items-center justify-center text-center rounded-lg text-sm font-bold cursor-pointer border-2 transition-all',
                    value === o.value ? 'bg-amber-600 border-amber-600 text-white' : 'background border-color-200 text-color-400 hover:border-amber-300')}>
                {t(o.label)}
            </button>
        ))}
    </div>
)

export const computeDefaultRowCount = (course, courseClass) => {
    const quota = courseClass ? courseClass.student_quota : course?.student_quota
    const count = courseClass ? courseClass.student_count : course?.student_count
    if (quota === null || quota === undefined) return 50
    return Math.max(0, Number(quota) - Number(count || 0))
}

// Card 1 dòng preview dùng chung khung (viền đỏ khi lỗi, badge Hợp lệ/Lỗi) - phần NỘI DUNG bên trong
// (children) khác nhau theo entity, mỗi Panel tự truyền vào.
// row.warnings (BE trả, mảng chuỗi tiếng Việt) = cảnh báo KHÔNG chặn - vd tên giáo xứ không khớp
// danh mục. Dòng vẫn Áp dụng bình thường, chỉ hiện dạng nhắc nhở màu hổ phách để admin xem lại.
export const PreviewRowCard = ({row, title, children, t}) => {
    const ok = row.status === 1
    const warnings = ok ? (row.warnings || []) : []
    return (
        <div className={classNames('p-2.5 flex flex-col gap-1 rounded-lg border', ok ? 'border-color-100' : 'border-rose-200 bg-rose-50')}>
            <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-color-000 truncate">
                    {ok ? (title || `#${row.row_number}`) : `#${row.row_number}`}
                </span>
                <span className={classNames('px-2 py-0.5 rounded-md text-2xs font-bold flex-shrink-0', ok ? 'bg-amber-50 text-amber-700' : 'bg-rose-100 text-rose-700')}>
                    {ok ? t('import row valid') : t('error')}
                </span>
            </div>
            {!ok && <span className="text-xs text-rose-600">{row.error_message}</span>}
            {ok && children}
            {warnings.map((w, i) => (
                <span key={i} className="text-2xs text-amber-600 leading-snug">⚠ {w}</span>
            ))}
        </div>
    )
}
